(() => {
  'use strict';

  if (window.ALIMMediaShowcase) {
    window.ALIMMediaShowcase.init(document);
    return;
  }

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class MediaShowcase {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-showcase-track]');
      this.slides = [...root.querySelectorAll('[data-showcase-slide]')];
      this.dots = [...root.querySelectorAll('[data-showcase-dot]')];
      this.current = root.querySelector('[data-showcase-current]');
      this.previous = root.querySelector('[data-showcase-prev]');
      this.next = root.querySelector('[data-showcase-next]');
      this.sticky = root.querySelector('[data-showcase-sticky]');
      this.index = 0;
      this.scrollFrame = 0;
      this.stickyFrame = 0;
      this.autoplayTimer = 0;
      this.destroyers = [];

      if (!this.track || !this.slides.length) return;
      this.bind();
      this.setActive(0, false);
      this.startAutoplay();
      this.updateSticky();
    }

    listen(target, event, callback, options) {
      target?.addEventListener(event, callback, options);
      this.destroyers.push(() => target?.removeEventListener(event, callback, options));
    }

    bind() {
      this.listen(this.track, 'scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = 0;
          const width = this.track.clientWidth || 1;
          this.setActive(Math.round(this.track.scrollLeft / width), false);
        });
      }, { passive: true });

      this.listen(this.track, 'keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.index + 1);
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.index - 1);
        }
      });

      this.listen(this.previous, 'click', () => this.goTo(this.index - 1));
      this.listen(this.next, 'click', () => this.goTo(this.index + 1));
      this.dots.forEach((dot) => this.listen(dot, 'click', () => this.goTo(Number(dot.dataset.showcaseDot))));

      const pause = () => this.stopAutoplay();
      const resume = () => this.startAutoplay();
      this.listen(this.root, 'pointerenter', pause);
      this.listen(this.root, 'pointerleave', resume);
      this.listen(this.root, 'focusin', pause);
      this.listen(this.root, 'focusout', resume);
      this.listen(this.track, 'touchstart', pause, { passive: true });
      this.listen(this.track, 'touchend', () => window.setTimeout(resume, 2500), { passive: true });
      this.listen(prefersReducedMotion, 'change', () => {
        this.stopAutoplay();
        this.startAutoplay();
      });

      if (this.sticky && this.root.dataset.stickyEnabled === 'true') {
        this.listen(window, 'scroll', () => this.queueSticky(), { passive: true });
        this.listen(window, 'resize', () => this.queueSticky(), { passive: true });
      }
    }

    goTo(index, smooth = true) {
      const total = this.slides.length;
      const nextIndex = ((index % total) + total) % total;
      this.track.scrollTo({
        left: nextIndex * this.track.clientWidth,
        behavior: smooth && !prefersReducedMotion.matches ? 'smooth' : 'auto'
      });
      this.setActive(nextIndex, false);
    }

    setActive(index, moveTrack) {
      this.index = Math.max(0, Math.min(index, this.slides.length - 1));
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        const video = slide.querySelector('video');
        if (!video) return;
        if (active && this.root.dataset.videoAutoplay === 'true') video.play().catch(() => {});
        if (!active) video.pause();
      });

      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      if (this.current) this.current.textContent = String(this.index + 1).padStart(2, '0');
      if (moveTrack) this.goTo(this.index);
    }

    startAutoplay() {
      this.stopAutoplay();
      if (this.root.dataset.galleryAutoplay !== 'true' || this.slides.length < 2 || prefersReducedMotion.matches || document.hidden) return;
      const delay = Math.max(3000, Number(this.root.dataset.galleryInterval) || 5000);
      this.autoplayTimer = window.setInterval(() => this.goTo(this.index + 1), delay);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = 0;
    }

    queueSticky() {
      if (this.stickyFrame) return;
      this.stickyFrame = requestAnimationFrame(() => {
        this.stickyFrame = 0;
        this.updateSticky();
      });
    }

    updateSticky() {
      if (!this.sticky || this.root.dataset.stickyEnabled !== 'true') return;
      const heroPassed = this.root.getBoundingClientRect().bottom < 90;
      const footer = document.querySelector('.site-footer');
      const footerReached = footer ? footer.getBoundingClientRect().top < window.innerHeight + 20 : false;
      const mobile = window.matchMedia('(max-width: 800px)').matches;
      const visible = mobile && heroPassed && !footerReached && !document.body.classList.contains('is-locked');
      this.sticky.classList.toggle('is-visible', visible);
      this.sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    selectBlock(block) {
      const slide = block?.closest?.('[data-showcase-slide]');
      if (!slide || !this.root.contains(slide)) return;
      this.stopAutoplay();
      this.goTo(Number(slide.dataset.slideIndex), false);
    }

    destroy() {
      this.stopAutoplay();
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.stickyFrame);
      this.destroyers.forEach((destroy) => destroy());
      this.root._alimMediaShowcase = null;
    }
  }

  const api = {
    init(scope = document) {
      const roots = [];
      if (scope.matches?.('[data-alim-showcase]')) roots.push(scope);
      roots.push(...scope.querySelectorAll?.('[data-alim-showcase]') || []);
      roots.forEach((root) => {
        if (root._alimMediaShowcase) return;
        root._alimMediaShowcase = new MediaShowcase(root);
      });
    }
  };

  window.ALIMMediaShowcase = api;

  const boot = () => api.init(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  document.addEventListener('shopify:section:load', (event) => api.init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-alim-showcase]')
      ? event.target
      : event.target.querySelector?.('[data-alim-showcase]');
    root?._alimMediaShowcase?.destroy();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest?.('[data-alim-showcase]');
    root?._alimMediaShowcase?.selectBlock(event.target);
  });
})();
